import { WeaponSlot, type WeaponDef } from '@vm/shared';
import type { CosmeticDef } from './cosmetics';

/**
 * Weapon sights ("scopes"). Every scope is a sidegrade for the weapon class it fits, never a straight upgrade:
 * more zoom costs aiming move speed and field of view, and the full-screen scope mask hides everything around the
 * crosshair. Different looks of the same kind (the three dot sights) have identical stats on purpose.
 *
 * The stock sight ('none', "Factory") is the weapon's own `zoom` / `scope` data, so owning no scope changes nothing.
 * Scopes are bought with COINS (they change how a weapon plays, so they are earned, never sold for gems).
 *
 * The server sim and the client prediction BOTH call {@link aimSpeedMul}, so aiming speed can never desync. The server
 * reads the scope from the player's signed match ticket (`PlayerInfo.cosmetics.scopes`), which is validated against
 * ownership and weapon class when it is saved and again here (an invalid or unknown pick falls back to the stock sight).
 */

/** Which scopes a weapon may carry. Derived from the weapon, see {@link scopeClassFor}. */
export type ScopeClass = 'sidearm' | 'shotgun' | 'smg' | 'rifle' | 'sniper';

/** Reticle drawn while aiming through a scope that has no full-screen mask. */
export type Reticle = 'dot' | 'ring' | 'chevron' | 'cross';

/** Movement multiplier while aiming with the stock sight (the long-standing 0.8 for every weapon that can aim). */
export const STOCK_ADS_SPEED = 0.8;

export interface ScopeDef extends CosmeticDef {
  /** Field-of-view multiplier while aiming (<1 zooms in). null = the weapon's own zoom. */
  zoom: number | null;
  /** Move-speed multiplier while aiming. null = {@link STOCK_ADS_SPEED}. */
  adsSpeed: number | null;
  /** Full-screen scope mask (can't see around the crosshair). null = whatever the weapon does by default. */
  masked: boolean | null;
  /** Reticle to draw instead of the normal crosshair while aiming (unmasked scopes only). */
  reticle: Reticle | null;
  /** Weapon classes that can carry this scope. */
  classes: readonly ScopeClass[];
  /** One line for the store/armory describing the trade-off. */
  blurb: string;
}

const ALL_NON_SNIPER: readonly ScopeClass[] = ['sidearm', 'shotgun', 'smg', 'rifle'];

export const SCOPES: ScopeDef[] = [
  { id: 'none', name: 'Factory Sights', rarity: 'common', price: 0, currency: 'coins', unlockLevel: 1, source: 'starter', zoom: null, adsSpeed: null, masked: null, reticle: null, classes: ['sidearm', 'shotgun', 'smg', 'rifle', 'sniper'], blurb: "The weapon's own sights." },
  // Dot sights: three looks, ZERO stat difference from each other or from the factory sights (zoom and aiming speed stay the
  // weapon's own). They only swap the crosshair you see while aiming, so nobody can buy an edge with one.
  { id: 'red_dot', name: 'Red Dot', rarity: 'common', price: 800, currency: 'coins', unlockLevel: 3, source: 'shop', zoom: null, adsSpeed: null, masked: false, reticle: 'dot', classes: ALL_NON_SNIPER, blurb: 'A clear red dot to aim with. Looks only: same zoom and speed as the weapon\'s own sights.' },
  { id: 'ring_dot', name: 'Ring Dot', rarity: 'common', price: 800, currency: 'coins', unlockLevel: 3, source: 'shop', zoom: null, adsSpeed: null, masked: false, reticle: 'ring', classes: ALL_NON_SNIPER, blurb: 'Looks only: a ring around the dot.' },
  { id: 'chevron', name: 'Chevron Sight', rarity: 'common', price: 1000, currency: 'coins', unlockLevel: 5, source: 'shop', zoom: null, adsSpeed: null, masked: false, reticle: 'chevron', classes: ALL_NON_SNIPER, blurb: 'Looks only: an arrow tip.' },
  // Magnified: more zoom costs aiming speed.
  { id: 'scope_2x', name: '2x Scope', rarity: 'rare', price: 1800, currency: 'coins', unlockLevel: 7, source: 'shop', zoom: 0.5, adsSpeed: 0.72, masked: false, reticle: 'cross', classes: ['smg', 'rifle'], blurb: 'Twice the zoom, slower while aiming.' },
  { id: 'scope_3x', name: '3x Scope', rarity: 'epic', price: 3000, currency: 'coins', unlockLevel: 12, source: 'shop', zoom: 0.33, adsSpeed: 0.58, masked: true, reticle: null, classes: ['rifle'], blurb: 'Reach far targets, but the scope hides everything around the crosshair.' },
  // Sniper scopes.
  { id: 'marksman', name: 'Marksman Scope', rarity: 'rare', price: 2200, currency: 'coins', unlockLevel: 9, source: 'shop', zoom: 0.55, adsSpeed: 0.86, masked: true, reticle: null, classes: ['sniper'], blurb: 'Wider view and quicker while aiming, but less zoom than the weapon\'s own scope.' },
  { id: 'long_range', name: 'Long-Range Scope', rarity: 'epic', price: 3500, currency: 'coins', unlockLevel: 14, source: 'shop', zoom: 0.2, adsSpeed: 0.5, masked: true, reticle: null, classes: ['sniper'], blurb: 'Huge zoom, but slow to move while aiming.' },
];

export const SCOPE_BY_ID: Record<string, ScopeDef> = Object.fromEntries(SCOPES.map((s) => [s.id, s]));

/** Magnification label for a field-of-view multiplier: 0.5 -> "2x", 0.33 -> "3x", 0.55 -> "1.8x". Shown in the Armory and store. */
export function zoomLabel(zoom: number): string {
  const x = Math.round((1 / Math.max(0.05, zoom)) * 10) / 10;
  return `${Number.isInteger(x) ? x : x.toFixed(1)}x`;
}

/** Which scopes a weapon accepts, or null when it has no aim-down-sights sight to swap. */
export function scopeClassFor(w: WeaponDef): ScopeClass | null {
  if (w.hidden || !w.zoom) return null;
  const pellets = (w.pellets ?? 1) > 1;
  if (w.slot === WeaponSlot.Sniper) return 'sniper';
  if (w.slot === WeaponSlot.Backup) return w.kind === 'hitscan' ? (pellets ? 'shotgun' : 'sidearm') : null;
  if (w.slot === WeaponSlot.Primary) {
    if (pellets) return 'shotgun';
    if (w.kind === 'hitscan' && w.range < 60) return 'smg';
    return 'rifle';
  }
  return null;
}

/** Scopes a weapon can carry, stock sights first. */
export function scopesFor(w: WeaponDef): ScopeDef[] {
  const cls = scopeClassFor(w);
  return cls ? SCOPES.filter((s) => s.classes.includes(cls)) : [];
}

export interface Sight {
  scopeId: string;
  /** Field-of-view multiplier while aiming. */
  zoom: number;
  /** Move-speed multiplier while aiming. */
  adsSpeed: number;
  /** Full-screen scope mask while aiming. */
  masked: boolean;
  reticle: Reticle | null;
}

/**
 * The sight a weapon actually has with `scopeId` fitted. Unknown ids, scopes the weapon's class cannot carry, and
 * weapons that cannot aim all resolve to the stock sight, so callers never need to validate.
 */
export function sightFor(w: WeaponDef | null | undefined, scopeId?: string | null): Sight {
  const stock: Sight = { scopeId: 'none', zoom: w?.zoom ?? 1, adsSpeed: w?.zoom ? STOCK_ADS_SPEED : 1, masked: !!w?.scope, reticle: null };
  if (!w || !w.zoom || !scopeId || scopeId === 'none') return stock;
  const s = SCOPE_BY_ID[scopeId];
  const cls = scopeClassFor(w);
  if (!s || !cls || !s.classes.includes(cls)) return stock;
  return {
    scopeId: s.id,
    zoom: s.zoom ?? stock.zoom,
    adsSpeed: s.adsSpeed ?? stock.adsSpeed,
    masked: s.masked ?? stock.masked,
    reticle: s.reticle,
  };
}

/** Movement multiplier from aiming. 1 when not aiming or when the weapon cannot aim. Used by the sim AND client prediction. */
export function aimSpeedMul(w: WeaponDef | null | undefined, scopeId: string | null | undefined, aiming: boolean): number {
  if (!aiming || !w?.zoom) return 1;
  return sightFor(w, scopeId).adsSpeed;
}
